import { useState } from 'react';
import { useAuthStore } from '../stores/auth';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../utils/api';
import { cn } from '../utils/cn';
import {
  User,
  Lock,
  Bell,
  Shield,
  Palette,
  Globe,
  CreditCard,
  Key,
  Smartphone,
  Mail,
  ChevronRight,
  Check,
  Eye,
  EyeOff,
  LogOut,
  Trash2,
  Download,
  Upload,
} from 'lucide-react';
import TwoFactorSetup from '../components/TwoFactorSetup';

const tabs = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'security', label: 'Security', icon: Lock },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'api', label: 'API Keys', icon: Key },
  { id: 'appearance', label: 'Appearance', icon: Palette },
];

export default function Settings() {
  const { user, logout } = useAuthStore();
  const [activeTab, setActiveTab] = useState('profile');
  const [showPassword, setShowPassword] = useState(false);
  
  // Profile state
  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    timezone: 'UTC',
    language: 'en',
  });
  
  // Security state
  const [security, setSecurity] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    twoFactor: false,
  });
  
  // Notification preferences
  const [notifications, setNotifications] = useState({
    emailTrades: true,
    emailSecurity: true,
    emailNews: false,
    pushPriceAlerts: true,
    pushBotAlerts: true,
    pushWorkflow: true,
    telegramEnabled: false,
  });

  // API Keys state
  const [apiKeys, setApiKeys] = useState([
    { id: '1', name: 'Main API Key', key: 'hx_live_...abc123', created: '2024-01-15', lastUsed: '2024-01-20' },
    { id: '2', name: 'Bot API Key', key: 'hx_live_...def456', created: '2024-01-10', lastUsed: '2024-01-19' },
  ]);

  const updateProfileMutation = useMutation({
    mutationFn: (data: typeof profile) => api.put('/auth/profile', data),
    onSuccess: () => toast.success('Profile updated'),
    onError: () => toast.error('Failed to update profile'),
  });

  const changePasswordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) => 
      api.put('/auth/password', data),
    onSuccess: () => {
      toast.success('Password changed');
      setSecurity({ ...security, currentPassword: '', newPassword: '', confirmPassword: '' });
    },
    onError: () => toast.error('Failed to change password'),
  });

  const renderProfile = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Profile Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#848e9c] mb-2">Display Name</label>
            <input
              type="text"
              value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              className="input"
              placeholder="Your name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#848e9c] mb-2">Email Address</label>
            <input
              type="email"
              value={profile.email}
              onChange={(e) => setProfile({ ...profile, email: e.target.value })}
              className="input"
              placeholder="your@email.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#848e9c] mb-2">Timezone</label>
            <select
              value={profile.timezone}
              onChange={(e) => setProfile({ ...profile, timezone: e.target.value })}
              className="select"
            >
              <option value="UTC">UTC</option>
              <option value="America/New_York">Eastern Time</option>
              <option value="America/Los_Angeles">Pacific Time</option>
              <option value="Europe/London">London</option>
              <option value="Asia/Tokyo">Tokyo</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#848e9c] mb-2">Language</label>
            <select
              value={profile.language}
              onChange={(e) => setProfile({ ...profile, language: e.target.value })}
              className="select"
            >
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="zh">中文</option>
              <option value="ja">日本語</option>
            </select>
          </div>
        </div>
        <div className="mt-6">
          <button
            onClick={() => updateProfileMutation.mutate(profile)}
            disabled={updateProfileMutation.isPending}
            className="btn-primary btn-md"
          >
            {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="pt-6 border-t border-white/[0.06]">
        <h3 className="text-lg font-semibold text-white mb-4">Account Actions</h3>
        <div className="flex gap-3">
          <button className="btn-secondary btn-md">
            <Download className="h-4 w-4" />
            Export Data
          </button>
          <button onClick={logout} className="btn-ghost btn-md text-[#f6465d] hover:bg-[#f6465d]/10">
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>
      </div>
    </div>
  );

  const renderSecurity = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Change Password</h3>
        <div className="max-w-md space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#848e9c] mb-2">Current Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={security.currentPassword}
                onChange={(e) => setSecurity({ ...security, currentPassword: e.target.value })}
                className="input pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#848e9c] hover:text-white"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#848e9c] mb-2">New Password</label>
            <input
              type="password"
              value={security.newPassword}
              onChange={(e) => setSecurity({ ...security, newPassword: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#848e9c] mb-2">Confirm New Password</label>
            <input
              type="password"
              value={security.confirmPassword}
              onChange={(e) => setSecurity({ ...security, confirmPassword: e.target.value })}
              className="input"
            />
          </div>
          <button
            onClick={() => changePasswordMutation.mutate({
              currentPassword: security.currentPassword,
              newPassword: security.newPassword,
            })}
            disabled={!security.currentPassword || !security.newPassword || security.newPassword !== security.confirmPassword}
            className="btn-primary btn-md"
          >
            Update Password
          </button>
        </div>
      </div>

      <div className="pt-6 border-t border-white/[0.06]">
        <h3 className="text-lg font-semibold text-white mb-4">Two-Factor Authentication</h3>
        <TwoFactorSetup />
      </div>

      <div className="pt-6 border-t border-white/[0.06]">
        <h3 className="text-lg font-semibold text-white mb-4">Sessions</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-4 bg-[#181a20] rounded-xl">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#0ecb81]/10 flex items-center justify-center">
                <Globe className="h-5 w-5 text-[#0ecb81]" />
              </div>
              <div>
                <div className="font-medium text-white">Current Session</div>
                <div className="text-sm text-[#848e9c]">Chrome on macOS • Active now</div>
              </div>
            </div>
            <span className="badge-success">Current</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotifications = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Email Notifications</h3>
        <div className="space-y-3">
          {[
            { key: 'emailTrades', label: 'Trade Confirmations', desc: 'Get notified when orders are filled' },
            { key: 'emailSecurity', label: 'Security Alerts', desc: 'Login attempts and security events' },
            { key: 'emailNews', label: 'News & Updates', desc: 'Platform updates and announcements' },
          ].map((item) => (
            <div key={item.key} className="flex items-center justify-between p-4 bg-[#181a20] rounded-xl">
              <div>
                <div className="font-medium text-white">{item.label}</div>
                <div className="text-sm text-[#848e9c]">{item.desc}</div>
              </div>
              <button
                onClick={() => setNotifications({ ...notifications, [item.key]: !notifications[item.key as keyof typeof notifications] })}
                className={cn(
                  "w-12 h-7 rounded-full transition-colors relative",
                  notifications[item.key as keyof typeof notifications] ? "bg-[#0ecb81]" : "bg-[#2b3139]"
                )}
              >
                <div className={cn(
                  "absolute top-1 w-5 h-5 rounded-full bg-white transition-transform",
                  notifications[item.key as keyof typeof notifications] ? "translate-x-6" : "translate-x-1"
                )} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="pt-6 border-t border-white/[0.06]">
        <h3 className="text-lg font-semibold text-white mb-4">Push Notifications</h3>
        <div className="space-y-3">
          {[
            { key: 'pushPriceAlerts', label: 'Price Alerts', desc: 'When prices hit your targets' },
            { key: 'pushBotAlerts', label: 'Bot Alerts', desc: 'Bot trades and status changes' },
            { key: 'pushWorkflow', label: 'Workflow Alerts', desc: 'Workflow execution results' },
          ].map((item) => (
            <div key={item.key} className="flex items-center justify-between p-4 bg-[#181a20] rounded-xl">
              <div>
                <div className="font-medium text-white">{item.label}</div>
                <div className="text-sm text-[#848e9c]">{item.desc}</div>
              </div>
              <button
                onClick={() => setNotifications({ ...notifications, [item.key]: !notifications[item.key as keyof typeof notifications] })}
                className={cn(
                  "w-12 h-7 rounded-full transition-colors relative",
                  notifications[item.key as keyof typeof notifications] ? "bg-[#0ecb81]" : "bg-[#2b3139]"
                )}
              >
                <div className={cn(
                  "absolute top-1 w-5 h-5 rounded-full bg-white transition-transform",
                  notifications[item.key as keyof typeof notifications] ? "translate-x-6" : "translate-x-1"
                )} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderApiKeys = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">API Keys</h3>
          <p className="text-sm text-[#848e9c]">Manage your API keys for programmatic access</p>
        </div>
        <button className="btn-primary btn-md">
          <Key className="h-4 w-4" />
          Create New Key
        </button>
      </div>

      <div className="space-y-3">
        {apiKeys.map((key) => (
          <div key={key.id} className="p-4 bg-[#181a20] rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#f0b90b]/10 flex items-center justify-center">
                  <Key className="h-5 w-5 text-[#f0b90b]" />
                </div>
                <div>
                  <div className="font-medium text-white">{key.name}</div>
                  <div className="text-sm text-[#848e9c] font-mono">{key.key}</div>
                </div>
              </div>
              <button className="btn-ghost btn-sm text-[#f6465d] hover:bg-[#f6465d]/10">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-center gap-6 text-xs text-[#5e6673]">
              <span>Created: {key.created}</span>
              <span>Last used: {key.lastUsed}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="p-4 bg-[#f0b90b]/10 rounded-xl">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 text-[#f0b90b] flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-medium text-[#f0b90b]">Keep your API keys secure</div>
            <div className="text-sm text-[#f0b90b]/80 mt-1">
              Never share your API keys. They provide full access to your account.
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAppearance = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Theme</h3>
        <div className="grid grid-cols-3 gap-4">
          {['Dark', 'Light', 'System'].map((theme) => (
            <button
              key={theme}
              className={cn(
                "p-4 rounded-xl border-2 transition-all",
                theme === 'Dark'
                  ? "border-[#f0b90b] bg-[#f0b90b]/5"
                  : "border-white/[0.08] hover:border-white/[0.15]"
              )}
            >
              <div className="w-full h-20 rounded-lg bg-[#181a20] mb-3" />
              <div className="text-sm font-medium text-white">{theme}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="pt-6 border-t border-white/[0.06]">
        <h3 className="text-lg font-semibold text-white mb-4">Chart Preferences</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#848e9c] mb-2">Default Chart Type</label>
            <select className="select max-w-xs">
              <option>Candlestick</option>
              <option>Line</option>
              <option>Area</option>
              <option>Bars</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#848e9c] mb-2">Default Timeframe</label>
            <select className="select max-w-xs">
              <option>1 Hour</option>
              <option>4 Hours</option>
              <option>1 Day</option>
              <option>1 Week</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-1">Settings</h1>
        <p className="text-[#848e9c]">Manage your account preferences</p>
      </div>

      <div className="flex gap-8">
        {/* Sidebar */}
        <div className="w-56 flex-shrink-0">
          <div className="card p-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                    activeTab === tab.id
                      ? "bg-[#f0b90b]/10 text-[#f0b90b]"
                      : "text-[#848e9c] hover:bg-white/[0.04] hover:text-white"
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1">
          <div className="card p-6">
            {activeTab === 'profile' && renderProfile()}
            {activeTab === 'security' && renderSecurity()}
            {activeTab === 'notifications' && renderNotifications()}
            {activeTab === 'api' && renderApiKeys()}
            {activeTab === 'appearance' && renderAppearance()}
          </div>
        </div>
      </div>
    </div>
  );
}
